import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  Info,
  LifeBuoy,
  LogOut,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  currencyForTimeZone,
  localTimeZone,
  type SupportedCurrencyCode,
} from "@repo/api-schema";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Section } from "../components/ui/section";
import { PageHeader } from "../components/ui/page-header";
import { CurrencyCombobox } from "../components/CurrencyCombobox";
import { useAuth } from "../auth/AuthContext";
import {
  useDeleteMe,
  useMe,
  useMyCredits,
  useUpdateMe,
  useUpdateSettings,
} from "../api/hooks";
import { getInitials } from "../lib/initials";
import type { User } from "../api/types";

/**
 * Combined account page reached from the header avatar. Rolls together what
 * used to be split across the avatar dropdown (edit profile, settings, about,
 * sign out) into one screen.
 */
export function ProfilePage(): ReactNode {
  const { data: me, isLoading } = useMe();

  const navigate = useNavigate();

  return (
    <div className="mx-auto max-w-xl space-y-stack-lg">
      <PageHeader back={{ label: "Back", onClick: () => navigate("/") }} />

      {isLoading || !me ? (
        <ProfileSkeleton />
      ) : (
        <>
          <ProfileHeader user={me} />
          <Section title="Profile">
            <DisplayNameForm user={me} />
          </Section>
          <Section
            title="Preferences"
            description="Your preferred currency totals what you're owed across groups on the home screen."
          >
            <PreferredCurrencyForm preferredCurrency={me.preferredCurrency} />
          </Section>
          <Section
            title="Credits"
            description="Spent when you scan a receipt. Earn more by watching a short video in the Tailspend mobile app."
          >
            <CreditsSummary />
          </Section>
          <Section title="More">
            <AccountLinks />
          </Section>
          <Section
            title="Danger zone"
            description="Permanently delete your account and all of your data. This cannot be undone."
          >
            <DeleteAccount />
          </Section>
        </>
      )}
    </div>
  );
}

function DeleteAccount(): ReactNode {
  const { logout } = useAuth();
  const deleteMe = useDeleteMe();
  const [confirming, setConfirming] = useState(false);

  async function onDelete(): Promise<void> {
    await deleteMe.mutateAsync();
    // Account is gone; clear the session and redirect to the signed-out state.
    logout();
  }

  // A sole-owner-of-a-shared-group block comes back as 409; surface a specific
  // hint so the user knows the remedy, otherwise a generic failure message.
  const isConflict =
    deleteMe.error !== null &&
    typeof deleteMe.error === "object" &&
    "error" in deleteMe.error &&
    (deleteMe.error as { error?: { code?: string } }).error?.code ===
      "CONFLICT";

  if (!confirming) {
    return (
      <Button
        variant="destructive"
        onClick={() => setConfirming(true)}
        className="gap-2"
      >
        <Trash2 className="size-4" />
        Delete account
      </Button>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-body-md text-on-surface-variant">
        This permanently deletes your profile, credits, and group memberships.
        Groups you solely own with other members must be transferred or deleted
        first.
      </p>
      {deleteMe.error ? (
        <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
          {isConflict
            ? "You're the sole owner of a group with other members. Transfer ownership or delete that group first."
            : "Couldn't delete your account. Please try again."}
        </div>
      ) : null}
      <div className="flex justify-end gap-3">
        <Button
          variant="ghost"
          onClick={() => setConfirming(false)}
          disabled={deleteMe.isPending}
        >
          Keep account
        </Button>
        <Button
          variant="destructive"
          onClick={onDelete}
          disabled={deleteMe.isPending}
        >
          {deleteMe.isPending ? "Deleting…" : "Delete forever"}
        </Button>
      </div>
    </div>
  );
}

function ProfileHeader({ user }: { user: User }): ReactNode {
  const initials = getInitials(user.displayName);
  return (
    <div className="flex items-center gap-4">
      <span
        aria-hidden="true"
        className="grid size-14 shrink-0 place-items-center rounded-full bg-primary text-on-primary text-headline-md"
      >
        {initials}
      </span>
      <div className="min-w-0">
        <h1 className="truncate text-headline-lg">{user.displayName}</h1>
        {user.email ? (
          <p className="truncate text-body-md text-on-surface-variant">
            {user.email}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function DisplayNameForm({ user }: { user: User }): ReactNode {
  const updateMe = useUpdateMe();
  const [displayName, setDisplayName] = useState(user.displayName);
  const dirty = displayName !== user.displayName;

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!dirty) return;
    await updateMe.mutateAsync({ displayName });
  }

  return (
    <form className="space-y-5" onSubmit={onSubmit}>
      <div className="space-y-2">
        <Label htmlFor="profile-display-name">Display name</Label>
        <Input
          id="profile-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
          minLength={1}
          maxLength={64}
        />
        <p className="text-label-sm text-on-surface-variant">
          Shown to other members of every group you belong to.
        </p>
      </div>
      <div className="space-y-2">
        <Label>Email</Label>
        <p className="text-body-md text-on-surface-variant">{user.email}</p>
      </div>
      {updateMe.error ? (
        <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
          Couldn't save changes. Please try again.
        </div>
      ) : null}
      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          disabled={!dirty || !displayName || updateMe.isPending}
        >
          {updateMe.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

function PreferredCurrencyForm({
  preferredCurrency,
}: {
  preferredCurrency: SupportedCurrencyCode | undefined;
}): ReactNode {
  const updateSettings = useUpdateSettings();
  // When the user has never chosen a currency, pre-fill with the
  // timezone-derived suggestion so the displayed value matches what the home
  // screen already uses — saving then makes that choice explicit.
  const initial = preferredCurrency ?? currencyForTimeZone(localTimeZone());
  const [currency, setCurrency] = useState<SupportedCurrencyCode>(initial);
  const dirty = currency !== preferredCurrency;

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!dirty) return;
    await updateSettings.mutateAsync({ preferredCurrency: currency });
  }

  return (
    <form className="space-y-5" onSubmit={onSubmit}>
      <div className="space-y-2">
        <Label htmlFor="preferred-currency">Preferred currency</Label>
        <CurrencyCombobox
          id="preferred-currency"
          value={currency}
          onChange={setCurrency}
        />
        {preferredCurrency === undefined ? (
          <p className="text-label-sm text-on-surface-variant">
            Defaulted from your device's time zone. Save to confirm.
          </p>
        ) : null}
      </div>
      {updateSettings.error ? (
        <div className="rounded-sm bg-error-container px-4 py-3 text-label-md text-on-error-container">
          Couldn't save your settings. Please try again.
        </div>
      ) : null}
      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={!dirty || updateSettings.isPending}>
          {updateSettings.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

function CreditsSummary(): ReactNode {
  const { data, isLoading } = useMyCredits();
  const balance = data?.balance ?? 0;
  return (
    <div className="flex items-center justify-between rounded-md bg-surface-container px-4 py-3">
      <span className="inline-flex items-center gap-2 text-body-md text-on-surface-variant">
        <Sparkles className="size-5 text-primary" />
        Balance
      </span>
      <span className="text-headline-sm tabular-nums text-on-surface">
        {isLoading ? "…" : balance.toLocaleString()}
      </span>
    </div>
  );
}

function AccountLinks(): ReactNode {
  const { logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="-my-2 divide-y divide-outline-variant/30">
      <button
        type="button"
        onClick={() => navigate("/about")}
        className="flex w-full cursor-pointer items-center gap-3 py-3 text-left text-body-lg transition-colors hover:text-primary"
      >
        <Info className="size-5 text-on-surface-variant" />
        <span className="flex-1">About</span>
        <ChevronRight className="size-4 text-on-surface-variant" />
      </button>
      <button
        type="button"
        onClick={() => navigate("/support")}
        className="flex w-full cursor-pointer items-center gap-3 py-3 text-left text-body-lg transition-colors hover:text-primary"
      >
        <LifeBuoy className="size-5 text-on-surface-variant" />
        <span className="flex-1">Help &amp; support</span>
        <ChevronRight className="size-4 text-on-surface-variant" />
      </button>
      <button
        type="button"
        onClick={() => logout()}
        className="flex w-full cursor-pointer items-center gap-3 py-3 text-left text-body-lg text-error transition-colors hover:opacity-80"
      >
        <LogOut className="size-5" />
        <span className="flex-1">Sign out</span>
      </button>
    </div>
  );
}

function ProfileSkeleton(): ReactNode {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="size-14 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-52" />
        </div>
      </div>
      <Skeleton className="h-48 rounded-md" />
      <Skeleton className="h-40 rounded-md" />
    </div>
  );
}
